import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from '@angular/core';
import { VibrationsChart } from '../../models/charts/VibrationsChart';
import { VibrationsChartChannel } from '../../models/charts/VibrationsChartChannel';
import { CategoryScale, Chart, Filler, Legend, LinearScale, LineController, LineElement, LogarithmicScale, PointElement, ScatterController, Title, Tooltip } from 'chart.js';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartDataFft } from '../../models/charts/chart-data-fft';
import { FormatTransducerTypePipe } from '@shared/pipes/format-transducer-type.pipe';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { PPV } from '@shared/models/ppv';
import annotationPlugin from 'chartjs-plugin-annotation';
import { PonderationEnum } from '@shared/models/ponderation.enum';
import { VibrationsRecordService } from '@shared/services/records/vibrations-record.service';
import { ChartUtilsService } from '@shared/services/utilities/chart-utils.service';
import { PonderationService } from '@shared/services/ponderation/ponderation.service';
import { Standard } from '@shared/models/standard';
import { StandardGraphDataDto } from '@shared/models/charts/standard-graph-data';

@Component({
  selector: 'app-vibration-chart-horizontal',
  imports: [CommonModule, TranslateModule, NgSelectComponent, FormsModule, FormatTransducerTypePipe],
  templateUrl: './vibration-chart-horizontal.component.html',
  styleUrl: './vibration-chart-horizontal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VibrationChartHorizontalComponent implements OnInit, AfterViewInit {
  private readonly STANDARD_STORAGE_KEY = 'vibration-standard';
  private readonly VECTOR_SUM_STANDARD_CODES = new Set(['NP2074']);

  constructor(
    private el: ElementRef,
    private vibrationRecordsService: VibrationsRecordService,
    private cdr: ChangeDetectorRef, 
    private translate: TranslateService,
    private chartUtils: ChartUtilsService,
    private ponderationService: PonderationService
  ) {
    // Registering necessary components for Chart.js
    Chart.register(
      CategoryScale,
      LinearScale,
      LogarithmicScale,
      Title,
      Tooltip,
      Legend,
      LineController,
      ScatterController,
      LineElement,
      PointElement,
      Filler,
      annotationPlugin
    );
  }

  @Input() siteId: number;
  @Input() eventId: number;
  @Input() vibrationRecord: VibrationsChart;
  @Input() ponderations: any[] = [];
  @Input() selectedPonderationId: number | null = null;
  selectedTab: string = 'signal';

  standards: Standard[] = [];
  selectedStandardCode: string | null = null;
  standardGraphData: StandardGraphDataDto | null = null;
  private standardChart: Chart | null = null;

  chart: Chart;
  chartDataFft: ChartDataFft[] = [];

  channelNames = ['VIBRATION.VERTICAL', 'VIBRATION.LONGITUDE', 'VIBRATION.TRANSVERSE'];
  ponderationTypes: any[] = [];
  ngOnInit(): void {
    if (this.ponderations.length === 1) {
      const singlePonderation = this.ponderations[0];
      this.selectedPonderationId = singlePonderation.ponderationId;
      this.onPonderationChange(singlePonderation);
    }
    this.ponderationService.getAllStandards().subscribe({
      next: (response) => {
        this.standards = response.data ?? [];

        const storedStandardCode = this.getStoredStandardCode();
        if (storedStandardCode) {
          const matchedStandard = this.standards.find(s => s.code === storedStandardCode);
          if (matchedStandard) {
            this.selectedStandardCode = matchedStandard.code;
            this.onStandardChange(matchedStandard);
          }
        }

        this.cdr.detectChanges();
      }
    });
  }

  private drawingVibrations(vibrationsData: VibrationsChart) {
    let minMax = this.findMinMax(vibrationsData);
    vibrationsData?.signalDataEntity?.signalData?.channels.forEach((chanel, indexChanel) => {
      if (!this.checkChannelData(chanel)) {
        if(vibrationsData.recordMetadata.recordType === 'Waveform'){
          this.drawingChannel(indexChanel, vibrationsData, minMax);
        }
        else if(vibrationsData.recordMetadata.recordType === 'Bargraph'){
          this.drawingChannel(indexChanel, vibrationsData, minMax);
        }

      }
    })

  }

  private checkChannelData(data: VibrationsChartChannel): boolean {
    let dataNullNumber = 0;
    data.timeSpectrum.values.forEach((v, i, a) => {
      if (v === 0) {
        dataNullNumber++;
      }
    });
    return dataNullNumber === data.timeSpectrum.values.length;
  }

  private findMinMax(vibration: VibrationsChart): number[] {
    let minMax = [0, 0, 0, 0];
    vibration?.signalDataEntity?.signalData?.channels.forEach((value, index, array) => {
      if (index < 3) {
        if (value.timeSpectrum.ppvMin < minMax[0]) minMax[0] = value.timeSpectrum.ppvMin;
        if (value.timeSpectrum.ppvMax > minMax[1]) minMax[1] = value.timeSpectrum.ppvMax;
      }
    })
    return minMax;
  }

  protected abs(value: number): number {
    return Math.abs(value);
  }
  public findMaxAbsValue(min: number, max: number) {
    return Math.abs(max) > Math.abs(min) ? max : Math.abs(min);
  }
  public findPPVMax(ppvs: PPV[]): number {
    if (!ppvs || ppvs.length === 0) return 0;
    const firstThreePpvs = ppvs.slice(0, 3);
    return Math.max(...firstThreePpvs.map(ppv => Math.abs(ppv.ppvMax)));
  }


private drawingChannel(index: number, vibrations: VibrationsChart, minMax: number[]) {
  const canvas = this.el.nativeElement.querySelector('#canvas-' + index) as HTMLCanvasElement;
  if (!canvas) {
    console.warn(`Canvas with ID 'canvas-${index}' not found.`);
    return;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const rawData = vibrations?.signalDataEntity?.signalData?.channels[index]?.timeSpectrum?.values || [];
  if (!rawData.length) {
    console.warn('No data available for channel', index);
    return;
  }

  const metadata = vibrations?.recordMetadata;
  const isBargraph = metadata?.recordType === 'Bargraph';
  const samplingFrequency = metadata?.samplingPeriod;

  let data: { x: number; y: number }[] = [];
  let totalDurationMs = 0;

  if (isBargraph) {

    if (metadata?.timestamp && metadata?.endDate) {
      const start = new Date(metadata.timestamp).getTime();
      const end = new Date(metadata.endDate).getTime();
      totalDurationMs = end - start;

    } else if (metadata?.recordLength && metadata.recordLength > 0) {
      totalDurationMs = metadata.recordLength * 1000;

    } else {
      console.error('No duration info for bargraph (endDate & recordLength missing)');
      return;
    }

    const step = totalDurationMs / rawData.length;

    data = rawData.map((value, idx) => ({
      x: idx * step,
      y: Math.abs(value as number)
    }));

  } 
  else {

    if (!samplingFrequency || samplingFrequency <= 0) {
      console.error('Invalid sampling frequency:', samplingFrequency);
      return;
    }

    data = rawData.map((value, idx) => ({
      x: (idx / samplingFrequency) * 1000,
      y: value as number
    }));

    totalDurationMs = (rawData.length / samplingFrequency) * 1000;
  }

  const maxAbsValue = Math.max(...data.map(d => Math.abs(d.y)));
  const maxIndex = data.findIndex(d => Math.abs(d.y) === maxAbsValue);
  const actualMaxValue = data[maxIndex]?.y ?? 0;
  const maxXValue = data[maxIndex]?.x ?? 0;

  const isMicroChannel =
    vibrations?.signalDataEntity?.signalData?.channels.lastIndexOf(
      vibrations?.signalDataEntity?.signalData?.channels[index]
    ) > 2;

  let sharedAbs: number;
  if (isMicroChannel) {
    const ownMin = vibrations?.signalDataEntity?.signalData?.channels[index]?.timeSpectrum?.ppvMin ?? 0;
    const ownMax = vibrations?.signalDataEntity?.signalData?.channels[index]?.timeSpectrum?.ppvMax ?? 0;
    sharedAbs = Math.max(Math.abs(ownMin), Math.abs(ownMax)) || 1;
  } else {
    sharedAbs = Math.max(Math.abs(minMax[0]), Math.abs(minMax[1])) || 1;
  }

  const yBound = Math.ceil(sharedAbs);
  const yMin = isBargraph ? 0 : -yBound;
  const yMax = yBound;

  const startTimestampMs = metadata?.timestamp ? new Date(metadata.timestamp).getTime() : 0;

  new Chart(ctx, {
    type: 'line',
    data: {
      datasets: [{
        label: 'Channel #' + (index + 1),
        data: data,
        fill: false,
        tension: 0.1,
        borderWidth: 1,
        borderColor: 'rgb(153,0,77)',
      }],
    },
    options: {
      elements: {
        point: { radius: 0 },
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        annotation: {
          annotations: [{
            type: 'point',
            xValue: maxXValue,
            yValue: actualMaxValue,
            radius: 5,
            borderColor: 'rgb(153,0,77)',
            borderWidth: 1
          }]
        },
        tooltip: {
          callbacks: isBargraph ? {
            title: (items: any[]) => {
              if (!items?.length) return '';
              const ms = Number(items[0].parsed.x);
              const d = new Date(startTimestampMs + ms);
              const pad = (n: number) => String(n).padStart(2, '0');
              return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
            }
          } : {}
        }
      },
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: totalDurationMs,
          ticks: {
            stepSize: totalDurationMs / 10,
            callback: function (val) {
              const ms = Number(val);
              if (isBargraph && startTimestampMs) {
                const d = new Date(startTimestampMs + ms);
                const pad = (n: number) => String(n).padStart(2, '0');
                return totalDurationMs >= 3600000
                  ? `${pad(d.getHours())}:${pad(d.getMinutes())}`
                  : `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
              }
              if (ms >= 3600000) return (ms / 3600000).toFixed(1) + ' h';
              if (ms >= 60000) return (ms / 60000).toFixed(1) + ' min';
              if (ms >= 1000) return (ms / 1000).toFixed(1) + ' s';
              return ms.toFixed(0) + ' ms';
            }
          }
        },
        y: {
          min: yMin,
          max: yMax,
          ticks: {
            stepSize: isMicroChannel ? 5 : 1,
            precision: 0
          },
          title: {
            display: true,
            text: isMicroChannel ? 'Pa' : 'mm / s'
          }
        }
      }
    }
  });
}
  setTab(tab: string): void {
    this.selectedTab = tab;
  }

  onStandardChange(standard: Standard | string | null): void {
    const standardCode = typeof standard === 'string' ? standard : standard?.code ?? null;

    this.selectedStandardCode = standardCode;
    this.storeStandardCode(standardCode);

    this.standardGraphData = null;
    if (this.standardChart) {
      this.standardChart.destroy();
      this.standardChart = null;
    }
    if (!standardCode || !this.siteId || !this.vibrationRecord?.id) return;
    this.vibrationRecordsService.getStandardGraphData(this.siteId, this.vibrationRecord.id, standardCode).subscribe({
      next: (response) => {
        this.standardGraphData = response.data;        
        if (this.standardGraphData?.weightedPpvValues) {
          this.vibrationRecord.timeSpectrumWeighted = this.standardGraphData.weightedPpvValues as any;
        }
        this.cdr.detectChanges();
        if (!this.standardGraphData?.weightedPpvValues && this.standardGraphData?.complianceChartData) {
          setTimeout(() => this.drawComplianceChart(), 0);
        }
      },
      error: (err) => console.error('Error loading standard graph data:', err)
    });
  }

  private getStoredStandardCode(): string | null {
    try {
      return localStorage.getItem(this.STANDARD_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private storeStandardCode(standardCode: string | null): void {
    try {
      if (standardCode) {
        localStorage.setItem(this.STANDARD_STORAGE_KEY, standardCode);
      } else {
        localStorage.removeItem(this.STANDARD_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors (private mode, quota, etc.)
    }
  }

  private drawComplianceChart(): void {
    if (this.standardChart) {
      this.standardChart.destroy();
      this.standardChart = null;
    }
    const canvas = this.el.nativeElement.querySelector('#canvas-compliance') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const chartData = this.standardGraphData!.complianceChartData!;

    const xMin = chartData.isLogX ? Math.log10(chartData.freqMin || 1) : chartData.freqMin;
    const xMax = chartData.isLogX ? Math.log10(chartData.freqMax) : chartData.freqMax;
    const yMin = chartData.velocityMin;
    const yMax = chartData.velocityMax;

    const datasets: any[] = chartData.criteriaLines.map((line) => ({
      label: line.name,
      data: line.points.map(p => ({
        x: this.toChartFrequencyValue(p.x, chartData.isLogX),
        y: p.y
      })),
      borderColor: line.color,
      backgroundColor: line.color,
      borderWidth: 2.5,
      borderDash: line.isDashed ? [6, 4] : [],
      pointRadius: 0,
      fill: false,
      tension: 0
    }));

    const channelShapes = ['circle', 'rectRot', 'rect'];
    const channelColors = ['rgba(255, 115, 119, 1)', 'rgba(61, 157, 165, 1)', 'rgba(125, 0, 61, 1)'];

    const measuredPoints = this.getMeasuredPointsForStandard(chartData.measuredPoints);

    measuredPoints.forEach((point, i) => {
      const color = channelColors[i] ?? point.color;
      datasets.push({
        label: point.channelName,
        data: [{
          x: this.toChartFrequencyValue(this.clamp(point.frequency, chartData.freqMin || 1, chartData.freqMax), chartData.isLogX),
          y: this.clamp(point.ppv, yMin, yMax),
          originalFrequency: point.frequency,
          originalPpv: point.ppv
        }],
        borderColor: color,
        backgroundColor: color,
        pointStyle: channelShapes[i] ?? 'circle',
        pointRadius: 4,
        pointHoverRadius: 7,
        showLine: false,
        isChannel: true
      });
    });

    const xTicks = this.generateLogFrequencyTicks(chartData.freqMin || 1, chartData.freqMax);

    const yTicks = this.generateDynamicTicks(
      chartData.velocityMin,
      chartData.velocityMax,
      chartData.isLogY
    );

    this.standardChart = new Chart(ctx, {
      type: 'scatter',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'nearest',
          intersect: false,
          axis: 'xy'
        },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              boxWidth: 12,
              filter: (item: any, data: any) => {
                return !!data.datasets[item.datasetIndex]?.isChannel;
              }
            },
            onClick: (_e: any, legendItem: any, legend: any) => {
              const ci = legend.chart;
              ci.data.datasets.forEach((ds: any, idx: number) => {
                if (ds.label === legendItem.text) {
                  ci.setDatasetVisibility(idx, !ci.isDatasetVisible(idx));
                }
              });
              ci.update();
            }
          },
          tooltip: {
            enabled: true,
            callbacks: {
              title: (items: any[]) => {
                if (!items?.length) return '';
                const ds = items[0].chart.data.datasets[items[0].datasetIndex];
                return ds.label ?? '';
              },
              label: (item: any) => {
                const ds = item.chart.data.datasets[item.datasetIndex];
                if (ds.isChannel) {
                  const raw = ds.data?.[0] ?? {};
                  const frequency = raw.originalFrequency ?? (chartData.isLogX ? Math.pow(10, item.parsed.x) : item.parsed.x);
                  const ppv = raw.originalPpv ?? item.parsed.y;
                  return [`Frequency: ${Number(frequency).toFixed(2)} Hz`, `PPV: ${Number(ppv).toFixed(2)} mm/s`];
                }
                return '';
              }
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            min: xMin,
            max: xMax,
            title: { display: true, text: 'Frequency (Hz)' },
            ticks: {
              ...(chartData.isLogX && {
                autoSkip: false,
                maxRotation: 0,
                minRotation: 0
              }),
              callback: (val: number) => {
                if (!chartData.isLogX) return val + ' Hz';
                const hz = Math.pow(10, val as number);
                const roundedHz = this.roundTick(hz);
                return xTicks.some(t => Math.abs(t - roundedHz) < 0.001) ? `${roundedHz} Hz` : null;
              }
            }
          },
          y: {
            type: chartData.isLogY ? 'logarithmic' : 'linear',
            min: yMin,
            max: yMax,
            title: { display: true, text: 'Velocity (mm/s)' },
            ...(chartData.isLogY && {
              afterBuildTicks: (axis: any) => {
                axis.ticks = yTicks.map(v => ({ value: v }));
              }
            }),
            ticks: {
              autoSkip: true,
              maxTicksLimit: 8,
              padding: 6,
              callback: (val: number) => {
                const numericValue = Number(val);
                return yTicks.includes(numericValue) ? numericValue + '' : null;
              }
            }
          }
        },
        showLine: true
      } as any
    });
  }

  getStandardMaxFrequencyLabel(): string {
    const max = this.standardGraphData?.complianceChartData?.freqMax;
    return max ? `${max} Hz` : '--';
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private toChartFrequencyValue(value: number, isLogX: boolean): number {
    return isLogX ? Math.log10(Math.max(value, 0.000001)) : value;
  }

  private roundTick(value: number): number {
    if (value >= 10) {
      return Math.round(value);
    }
    return Number(value.toFixed(2));
  }

  private generateLogFrequencyTicks(minValue: number, maxValue: number): number[] {
    const minPower = Math.floor(Math.log10(minValue));
    const maxPower = Math.ceil(Math.log10(maxValue));
    const ticks: number[] = [];

    for (let power = minPower; power <= maxPower; power++) {
      for (let multiplier = 1; multiplier <= 9; multiplier++) {
        const tickValue = multiplier * Math.pow(10, power);
        if (tickValue >= minValue && tickValue <= maxValue) {
          ticks.push(this.roundTick(tickValue));
        }
      }
    }

    return Array.from(new Set(ticks)).sort((a, b) => a - b);
  }

  private getMeasuredPointsForStandard(points: any[]): any[] {
    if (!this.shouldUseVectorSumForStandard()) {
      return points;
    }

    const vectorSum = this.getVectorSumPpv();
    const frequency = this.getLowestComponentFrequency(points);
    if (vectorSum == null || frequency == null) {
      return points;
    }

    return [{
      channelName: 'Vector Sum',
      frequency,
      ppv: vectorSum,
      color: 'rgba(125, 0, 61, 1)'
    }];
  }

  private shouldUseVectorSumForStandard(): boolean {
    const code = this.selectedStandardCode?.replace(/[\s_-]/g, '').toUpperCase();
    return !!code && this.VECTOR_SUM_STANDARD_CODES.has(code);
  }

  private getVectorSumPpv(): number | null {
    const meta = this.vibrationRecord?.recordMetadata;
    const vectorSum = Number(meta?.vecSomme ?? this.vibrationRecord?.vecSomme);
    if (Number.isFinite(vectorSum) && vectorSum > 0) {
      return Math.abs(vectorSum);
    }

    const components = [meta?.verticalPpv, meta?.longitudinalPpv, meta?.transversePpv]
      .map(value => Number(value))
      .filter(value => Number.isFinite(value));

    if (components.length === 3) {
      return Math.sqrt(components.reduce((sum, value) => sum + Math.pow(value, 2), 0));
    }

    return null;
  }

  private getLowestComponentFrequency(points: any[]): number | null {
    const channelFrequencies = this.vibrationRecord?.signalDataEntity?.signalData?.channels
      ?.slice(0, 3)
      .map(channel => Number(channel?.frequencySpectrum?.maximumFrequencyHz))
      .filter(value => Number.isFinite(value) && value > 0) ?? [];

    if (channelFrequencies.length > 0) {
      return Math.min(...channelFrequencies);
    }

    const pointFrequencies = points
      ?.slice(0, 3)
      .map(point => Number(point?.frequency))
      .filter(value => Number.isFinite(value) && value > 0) ?? [];

    if (pointFrequencies.length > 0) {
      return Math.min(...pointFrequencies);
    }

    const metadataFrequency = Number(this.vibrationRecord?.recordMetadata?.maxFrequency);
    return Number.isFinite(metadataFrequency) && metadataFrequency > 0 ? metadataFrequency : null;
  }

  private generateDynamicTicks(minValue: number, maxValue: number, isLog: boolean): number[] {
    if (!isLog) {
      const step = (maxValue - minValue) / 5;
      return Array.from({ length: 6 }, (_, i) => minValue + i * step);
    }

    const minPower = Math.floor(Math.log10(minValue));
    const maxPower = Math.ceil(Math.log10(maxValue));
    const ticks: number[] = [];
    const majorMultipliers = [1, 2, 3, 4, 5, 6, 7, 8, 9];

    for (let power = minPower; power <= maxPower; power++) {
      for (const multiplier of majorMultipliers) {
        const tickValue = multiplier * Math.pow(10, power);
        if (tickValue >= minValue && tickValue <= maxValue) {
          ticks.push(tickValue);
        }
      }
    }

    if (!ticks.some(t => Math.abs(t - minValue) < 0.001)) {
      ticks.push(minValue);
    }
    if (!ticks.some(t => Math.abs(t - maxValue) < 0.001)) {
      ticks.push(maxValue);
    }

    return ticks.sort((a, b) => a - b);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.drawingVibrations(this.vibrationRecord);
    }, 500);

    setTimeout(() => {
      this.drawFFTChart(this.vibrationRecord);
    }, 500);


  }

  private drawFFTChart(vibrations: VibrationsChart): void {
    if (this.chart) {
      this.chart.destroy();
    }

    const colorsArray = ['rgba(255, 115, 119, 1)', 'rgba(61, 157, 165, 1)', 'rgba(125, 0, 61, 1)', 'rgba(250, 199, 15, 1)'];

    let canvas = <HTMLCanvasElement>this.el.nativeElement.querySelector('#canvas-fft');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let vibrationData: any = this.formatVibrationData(vibrations);

    if (!vibrationData?.channels?.length) {
      return;
    }
    
    
    
    const filteredChannels = vibrationData.channels.slice(0, 3).map((channel: any) => {
      const filteredFrequencies: number[] = [];
      const filteredMagnitudes: number[] = [];
      for (let i = 0; i < channel.frequencySpectrum.frequencies.length; i++) {
        if (channel.frequencySpectrum.frequencies[i] > 0 && channel.frequencySpectrum.frequencies[i] <= 500) {
          filteredMagnitudes.push(channel.frequencySpectrum.magnitudes[i]);
          filteredFrequencies.push(channel.frequencySpectrum.frequencies[i]);
        }
      }

      return {
        name: channel.name,
        frequencySpectrum: {
          frequencies: filteredFrequencies,
          magnitudes: filteredMagnitudes
        }
      };
    });

    const datasets = filteredChannels.map((channel: any, index: number) => {
      const dataPoints = channel.frequencySpectrum.magnitudes.map((magnitude: number, i: number) => ({
        x: channel.frequencySpectrum.frequencies[i],
        y: magnitude
      }));
      
      return {
        label: `C${index + 1}`,
        data: dataPoints,
        borderColor: colorsArray[index],
        backgroundColor: colorsArray[index],
        fill: false,
        tension: 0,
        pointRadius: 0,
        borderWidth: 1,
        stepped: false
      };
    });
    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'logarithmic',
            title: {
              display: true,
              text: 'Fréquence (Hz)',
            },
            min: 1,
            ticks: {
              callback: (value) => {
                const numValue = Number(value);
                if (numValue < 200) {
                  if ([1, 2, 5, 10, 20, 50, 100, 200].includes(numValue)) {
                    return numValue + ' Hz';
                  }
                } else {
                  if ([250, 500, 1000, 1500].includes(numValue)) {
                    return numValue + ' Hz';
                  }
                }
                return null;
              }
            }
          },
          y: {
            title: {
              display: true,
              text: 'Magnitude',
            },
            beginAtZero: true,
          },
        },
        plugins: {
          tooltip: {
            enabled: true,
          },
          legend: {
            position: 'top',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              padding: 10,
            }
          },
        },
      },
    });
  }


  private formatVibrationData(Vibration: VibrationsChart) {
    const channels = [];
    for (let i = 0; i < Vibration?.signalDataEntity?.signalData?.channels.length; i++) {
      const frequencies = this.vibrationRecord?.signalDataEntity?.signalData?.channels[i].frequencySpectrum.frequencies;
      const magnitudes = this.vibrationRecord?.signalDataEntity?.signalData?.channels[i].frequencySpectrum.magnitudes;
      channels.push({
        name: `Channel ${i + 1}`,
        frequencySpectrum: {
          frequencies: frequencies,
          magnitudes: magnitudes
        }
      });
    }
    return { channels: channels };
  }
onPonderationChange(event: any): void {
    if (event) {
      if (this.siteId && this.vibrationRecord?.id) {
        this.vibrationRecordsService
          .GetRecordById(this.siteId, this.vibrationRecord.id,this.selectedPonderationId)
          .subscribe({
            next: (record) => {
              this.vibrationRecord = record.data;
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.error('Error fetching record:', err);
            }
          });
      }
    } else {
      this.vibrationRecord = null;
      this.cdr.detectChanges();
    }
  }

    pascalToDecibel(pressure: number): number | null {
    if(pressure == null) return null;
    const referencePressure = 20e-6;
    return 20 * Math.log10(pressure / referencePressure);
  }
    isNaN(value: any): boolean {
    return Number.isNaN(Number(value));
  }

}
